import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CardQuestLocation from "../../components/common/Card/CardQuestLocation";
import Search from "../../components/common/Field/Search";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import {
  useLazyGetFavoriteLocationsQuery,
  usePostFavoriteLocationMutation,
} from "../../services/apiSlice";
import Brand from "./Brand";

const Favorites = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [triggerGetFavorites, { data: favoritesData, isLoading: isFetching }] =
    useLazyGetFavoriteLocationsQuery();

  const [postFavorite] = usePostFavoriteLocationMutation();

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    triggerGetFavorites({ page: 1, limit: 100 });
  }, [triggerGetFavorites]);

  const handleFavoriteLocation = async (locationId) => {
    try {
      await postFavorite({ location_id: locationId }).unwrap();
      triggerGetFavorites({ page: 1, limit: 100 });
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
    }
  };

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [dragMoved, setDragMoved] = useState(false);

  const handleMouseDown = (e) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setDragMoved(false);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = "grabbing";
  };

  const handleMouseLeaveOrUp = () => {
    if (!scrollContainerRef.current) return;
    setIsDragging(false);
    scrollContainerRef.current.style.cursor = "grab";
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    setDragMoved(true);
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleClickCapture = (e) => {
    if (dragMoved) {
      e.stopPropagation();
    }
  };

  const rawOutlets = favoritesData?.data || [];
  const outlets = rawOutlets.filter((quest) => {
    if (!searchQuery) return true;
    const queryLower = searchQuery.toLowerCase();
    const businessName = quest.business?.company_name?.toLowerCase() || "";
    const address = quest.location?.name?.toLowerCase() || "";
    return businessName.includes(queryLower) || address.includes(queryLower);
  });

  const hasOutlets = outlets.length > 0;
  const handleGoBack = () => {
    navigate(`/quest`);
  };

  return (
    <>
      <LoadingCommon statusLoading={isFetching} />
      <div className="section-content flex flex-col py-[30px] sm:mt-6 w-full gap-6">
        <div className="flex flex-col gap-3">
          <div className="w-[42px] h-[42px] rounded-full">
            <div
              onClick={handleGoBack}
              className="w-full h-full flex items-center justify-center"
            >
              <span
                className={`icon icon-arrow-left text-[#A0A0A0] text-[18px]`}
              />
            </div>
          </div>
          <Search
            className="w-full max-w-full"
            placeholder={t("base.search")}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            showFavorites={false}
            showClear
          />
        </div>

        {searchQuery ? (
          <div className="flex flex-col gap-6">
            <h5 className="text-primary font-[Figtree] text-xl font-bold leading-none">
              Search Results ({outlets.length || 0})
            </h5>

            {hasOutlets ? (
              <div
                className="flex items-center gap-[18px] w-full overflow-x-auto pb-2 scrollbar-hide"
                ref={scrollContainerRef}
                style={{ cursor: "grab" }}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeaveOrUp}
                onMouseUp={handleMouseLeaveOrUp}
                onMouseMove={handleMouseMove}
                onClickCapture={handleClickCapture}
              >
                {outlets.map((quest, index) => (
                  <CardQuestLocation
                    key={index}
                    businessName={quest.business?.company_name || ""}
                    locationId={quest.location?.id}
                    isFavorited={true}
                    onFavoriteLocation={handleFavoriteLocation}
                    businessAvatar={
                      quest.business?.avatar_data?.file_path || ""
                    }
                    thumnail={quest.quest_businesses}
                    address={quest.location?.name}
                    handleNavigateToDetails={() =>
                      navigate(`/quest/location/${quest.location?.id}`)
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="text-gray-400 text-sm italic">
                {searchQuery
                  ? "No outlets match your search."
                  : "No favorite outlets yet."}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <span className="icon icon-heart-active text-primary text-lg" />
              <h1 className="text-primary font-[Figtree] text-xl font-bold leading-6">
                Favorites
              </h1>
            </div>

            <div className="flex flex-col gap-6">
              <h5 className="text-primary font-[Figtree] text-xl font-bold leading-none">
                Outlets
              </h5>

              {hasOutlets ? (
                <div
                  className="flex items-center gap-[18px] w-full overflow-x-auto pb-2 scrollbar-hide"
                  ref={scrollContainerRef}
                  style={{ cursor: "grab" }}
                  onMouseDown={handleMouseDown}
                  onMouseLeave={handleMouseLeaveOrUp}
                  onMouseUp={handleMouseLeaveOrUp}
                  onMouseMove={handleMouseMove}
                  onClickCapture={handleClickCapture}
                >
                  {outlets.map((quest, index) => (
                    <CardQuestLocation
                      key={index}
                      businessName={quest.business?.company_name || ""}
                      locationId={quest.location?.id}
                      isFavorited={true}
                      onFavoriteLocation={handleFavoriteLocation}
                      businessAvatar={
                        quest.business?.avatar_data?.file_path || ""
                      }
                      thumnail={quest.quest_businesses}
                      address={quest.location?.name}
                      handleNavigateToDetails={() =>
                        navigate(`/quest/location/${quest.location?.id}`)
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="text-gray-400 text-sm italic">
                  {searchQuery
                    ? "No outlets match your search."
                    : "No favorite outlets yet."}
                </div>
              )}
            </div>

            {/* <div className="flex flex-col gap-6">
              <h5 className="text-primary font-[Figtree] text-xl font-bold leading-6">Brands</h5>
            </div> */}
            <Brand />
          </>
        )}
      </div>
    </>
  );
};

export default Favorites;
